import type { OBB, Vec2 } from '../../geo/types';
import { obbCorners } from '../../geo/polygon';
import type { Lot, ReservedFootprint } from './types';
import { PAINTED_LASTAGE } from './paintedLastage';
import committed from './woodcut/woodcutReadings.json';

/** Houses the woodcut draws standing free in fields and yards, on no street: their count and stepped gables. */
export interface WoodcutGroup {
  readonly fronts: number;
  readonly stepped: number | null;
  readonly note: string;
}

function isCount(v: unknown): v is number {
  return Number.isInteger(v) && (v as number) >= 0;
}

export function parseWoodcutGroup(key: string, value: unknown): WoodcutGroup {
  const g = value as Partial<WoodcutGroup> | null | undefined;
  const ok =
    g !== null &&
    typeof g === 'object' &&
    isCount(g.fronts) &&
    (g.stepped === null || (isCount(g.stepped) && g.stepped <= g.fronts)) &&
    typeof g.note === 'string';
  if (!ok)
    throw new Error(
      `woodcutReadings.json: the group ${key} is not a front count, a stepped count within it and a note`
    );
  return { fronts: g.fronts, stepped: g.stepped, note: g.note };
}

const GROUP_KEY = 'lastage-keizersstraat-gardens';

export const KEIZERS_GARDENS: WoodcutGroup = parseWoodcutGroup(
  GROUP_KEY,
  (committed as { readonly groups?: Readonly<Record<string, unknown>> }).groups?.[GROUP_KEY]
);

/* The garden houses' source: they stand on no street, so none has a front street or a plot. */
const GARDEN_SOURCE = 'lastage-gardens';

export function isGardenHouse(lot: Pick<Lot, 'sourceId'>): boolean {
  return lot.sourceId.startsWith(`${GARDEN_SOURCE}/`);
}

function unit(v: Vec2): Vec2 {
  const length = Math.hypot(v[0], v[1]);
  return [v[0] / length, v[1] / length];
}

/* The painted feet of the houses, in the painter frame's pixels, as PAINTED_LASTAGE lists them. */
const FEET: readonly Vec2[] = [
  [488, 926],
  [499, 931],
  [519, 922],
  [519, 940],
  [541, 945],
];

function footOf(frame: Vec2): Vec2 {
  const building = PAINTED_LASTAGE.buildings.find(
    (b) => b.frame[0] === frame[0] && b.frame[1] === frame[1]
  );
  if (!building) throw new Error(`PAINTED_LASTAGE has no building at frame ${frame.join(', ')}`);
  return building.at;
}

const feet = FEET.map(footOf);
const middle: Vec2 = [
  feet.reduce((sum, p) => sum + p[0], 0) / feet.length,
  feet.reduce((sum, p) => sum + p[1], 0) / feet.length,
];
/* The painter camera's ground point (src/data/painterView.json, layout.position). */
const PAINTER_AT: Vec2 = [2411.26, 1437.54];

/**
 * PLAN Lots, the Lastage garden houses (2026-09-28). The layout drops the Keizersstraat: the
 * painting of 1538 shows gardens with ditches there and the woodcut of 1544 garden plots, tenter
 * frames and sheds, with about five houses in a row by the Sint Antoniespoort (woodcutReadings.json,
 * groups). The painting draws a cluster of buildings on that ground, their feet read in
 * PAINTED_LASTAGE; the five houses stand on the five feet that are a front's foot, the frames at
 * (532, 925) and (546, 930) lying on the roof of the building footed at (541, 945) and (502, 954)
 * on a long shed. The painting shows their fronts, so each faces the painter's standpoint from the
 * group's middle, and each is as wide and as deep as the median house the street carried on that
 * side (5.2 m by 10.3 m).
 */
export const GARDEN_HOUSES: {
  readonly facing: Vec2;
  readonly frontage: number;
  readonly depth: number;
  readonly feet: readonly Vec2[];
} = {
  facing: unit([PAINTER_AT[0] - middle[0], PAINTER_AT[1] - middle[1]]),
  frontage: 5.2,
  depth: 10.3,
  feet,
};

function gardenHouse(foot: Vec2, i: number, index: number): Lot {
  const { facing, frontage, depth } = GARDEN_HOUSES;
  const center: Vec2 = [foot[0] - (facing[0] * depth) / 2, foot[1] - (facing[1] * depth) / 2];
  const obb: OBB = {
    center,
    axisU: [facing[1], -facing[0]],
    halfU: frontage / 2,
    halfV: depth / 2,
  };
  return {
    index,
    sourceId: `${GARDEN_SOURCE}/house${i}`,
    side: 'left',
    ordinal: i,
    kind: 'house',
    obb,
    corners: obbCorners(obb),
    frontage,
    depth,
    facing,
  };
}

/** The garden houses, numbered from `startIndex`; the woodcut's count and the painted feet must agree. */
export function gardenHouseLots(startIndex: number): readonly Lot[] {
  if (GARDEN_HOUSES.feet.length !== KEIZERS_GARDENS.fronts) {
    throw new Error(
      `${GARDEN_HOUSES.feet.length} painted feet for the woodcut's ${KEIZERS_GARDENS.fronts} garden houses`
    );
  }
  return GARDEN_HOUSES.feet.map((foot, i) => gardenHouse(foot, i, startIndex + i));
}

/** Each garden house's footprint, which no other lot may take. */
export function gardenHouseReservations(): readonly ReservedFootprint[] {
  return gardenHouseLots(0).map((lot) => ({
    id: lot.sourceId,
    polygon: { outer: lot.corners, holes: [] },
  }));
}
